'use client';

import styles from '~/styles/components/author/author-titles.module.scss';
import { Title } from 'types';
import { useState, useEffect } from 'react';

import LoadableImage from '~/components/loadable-image';
import { images, titlePhotoPath } from 'sdk';
import Links from '~/lib/helpers/link-helper';
import Link from 'next/link';
import ArrowLeftBtnSVG from '~/assets/svg/icons/arrow-left-btn';
import ArrowRightBtnSVG from '~/assets/svg/icons/arrow-right-btn';
import TitleStarRating from '../title-star-rating';

function usePagination<D>(items: D[]) {
  const [currPage, setCurrPage] = useState(0);
  const [filteredItems, setFilteredItems] = useState<D[]>(items);
  const [pages, setPages] = useState<D[][]>([]);
  const [pageCount, setPageCount] = useState(0);

  function paginateItems(items: any, pageSize: number) {
    const pageCount = Math.ceil(items.length / pageSize);
    const pages = Array.from({ length: pageCount }, (_, i) => {
      const start = i * pageSize;
      return items.slice(start, start + pageSize);
    });
    pages.sort(
      (a, b) => b?.popularity?.copies_sold - a?.popularity?.copies_sold
    );
    return { pageCount, pages };
  }

  useEffect(() => {
    const { pageCount, pages } = paginateItems(filteredItems, 9);
    setPages(pages);
    setPageCount(pageCount);
  }, [filteredItems]);

  function nextPage() {
    if (currPage < pageCount - 1) {
      setCurrPage(currPage + 1);
    } else {
      setCurrPage(0);
    }
  }

  function prevPage() {
    if (currPage > 0) {
      setCurrPage(currPage - 1);
    } else {
      setCurrPage(pageCount - 1);
    }
  }

  return {
    pages,
    currPage,
    pageCount,
    nextPage,
    prevPage,
    setFilteredItems,
    setCurrPage,
  };
}

interface AuthorTitlesProps {
  author_name: string;
  titles: Title[];
  genres: any;
}
export default function AuthorTitles({
  author_name,
  titles,
  genres,
}: AuthorTitlesProps) {
  const new_genres = genres.slice();

  new_genres.sort((a, b) => a?.display - b?.display);
  new_genres.unshift({ display: 'All' });

  const [currCategory, setCurrCategory] = useState('All');

  const {
    pages,
    pageCount,
    currPage,
    prevPage,
    nextPage,
    setFilteredItems,
    setCurrPage,
  } = usePagination<Title>(titles);

  function handleCategory(category: string) {
    setCurrCategory(category);
    if (category == 'All') {
      setFilteredItems(titles);
      return;
    }
    const filtered = titles.filter((t) => t?.genre?.display === category);
    setFilteredItems(filtered);
    setCurrPage(0);
  }

  function renderTitle(t: Title) {
    return (
      <Link href={Links.title(t).show} passHref>
        <div className={styles['titles-listing']}>
          <div className={styles['titles-image']}>
            <LoadableImage
              src={images(titlePhotoPath(t)).book.thumb}
              width={100}
              height={100}
              alt={t?.title}
            />
          </div>

          <div className={styles['titles-details-container']}>
            <div className={styles['titles-titlename']}>
              <h3>{t?.title} </h3>
            </div>
            <div className={styles['titles-details']}>
              <p>
                <TitleStarRating title={t} type='author' />
              </p>
              {t?.copies > 0 && (
                <p>{t?.copies} available</p>
              )}
              {t?.popularity?.copies_sold > 0 && (
              <p>{t?.popularity?.copies_sold}  sold</p>
              )}
             
             
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className={styles['body']}>
      <h2>
        {currCategory} titles by {author_name}
      </h2>

      <div className={styles['filter']}>
        <div className={styles['filter-genre-row']}>
          {new_genres?.map((g) =>
            g?.display != currCategory ? (
              <p
                className={styles['filter-item']}
                onClick={() => handleCategory(g?.display)}
              >
                {g?.display}
              </p>
            ) : (
              <p
                className={styles['filter-item-select']}
                onClick={() => handleCategory(g?.display)}
              >
                {g?.display}
              </p>
            )
          )}
        </div>
      </div>
      <div className={styles['titles-grid']}>
        {pages[currPage]?.map((t) => renderTitle(t))}
      </div>

      {pageCount > 1 && (
        <div className={styles['pagination']}>
          <div className={styles['pagination-container']}>
            <button onClick={prevPage} aria-label='Previous page'>
              <ArrowLeftBtnSVG width={50} height={50} />
            </button>
            <div className={styles['pagination-carousel']}>
              {Array.from({ length: pageCount }).map((_, index) => (
                <button
                  key={index}
                  className={index === currPage ? styles['active'] : ''}
                  onClick={() => setCurrPage(index)}
                ></button>
              ))}
            </div>

            <button onClick={nextPage} aria-label='Next page'>
              <ArrowRightBtnSVG width={50} height={50} />
            </button>
          </div>
          <p>
            Page {currPage + 1} of {pageCount}
          </p>
        </div>
      )}
    </div>
  );
}
